"use client";
import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { useSelector } from "react-redux";

const CreateUserModal = ({ modal, toggle, onUserCreated }) => {
  const token = useSelector((state) => state.login.token);
  const [userType, setUserType] = useState("influencer"); // 'influencer' or 'business'
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Common fields
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    phoneNumber: "", // For business
    country: "",
    city: "",
    nationality: "", // For influencer
  });

  // Influencer specific fields
  const [influencerData, setInfluencerData] = useState({
    dateOfBirth: "",
    instagram: "",
    facebook: "",
    followers: "",
    categories: [],
    profileImage: null,
  });

  // Business specific fields (aligned with Tafaseel app: company name + phone only)
  const [businessData, setBusinessData] = useState({
    companyName: "",
  });

  const categories = [
    "Beauty",
    "Fashion",
    "Lifestyle",
    "Food & Cooking",
    "Travel",
    "Fitness & Health",
    "Technology",
    "Gaming",
    "Music",
    "Art & Design",
    "Photography",
    "Parenting",
    "Business",
    "Education",
    "Sports",
    "Entertainment",
    "Home & Garden",
    "Automotive",
    "Pets & Animals",
    "Books & Literature",
  ];

  const followersOptions = [
    { label: "Select Followers", value: "" },
    { label: "10,000+", value: "10000" },
    { label: "25,000+", value: "25000" },
    { label: "50,000+", value: "50000" },
    { label: "100,000+", value: "100000" },
    { label: "250,000+", value: "250000" },
    { label: "500,000+", value: "500000" },
    { label: "1,000,000+", value: "1000000" },
  ];

  // Countries and cities data (from LocationScreen - exact match)
  const countries = [
    { label: "Select Country", value: "" },
    { label: "Syria", value: "Syria" },
    { label: "UAE (Coming Soon)", value: "UAE (Coming Soon)", disabled: true },
  ];

  const cities = {
    Syria: [
      { label: "Select City", value: "" },
      { label: "Damascus", value: "Damascus" },
      { label: "Aleppo", value: "Aleppo" },
      { label: "Homs", value: "Homs" },
      { label: "Latakia", value: "Latakia" },
      { label: "Tartus", value: "Tartus" },
      { label: "Idlib", value: "Idlib" },
      { label: "Daraa", value: "Daraa" },
      { label: "Hama", value: "Hama" },
      { label: "Raqqa", value: "Raqqa" },
      { label: "Deir ez-Zor", value: "Deir ez-Zor" },
      { label: "Hasakah", value: "Hasakah" },
      { label: "Qamishli", value: "Qamishli" },
      { label: "Suwayda", value: "Suwayda" },
      { label: "Palmyra", value: "Palmyra" },
    ],
    "UAE (Coming Soon)": [{ label: "Coming Soon", value: "Coming Soon" }],
  };

  // Nationalities list (simplified - you can add more)
  const nationalities = [
    { label: "Select Nationality", value: "" },
    { label: "Syrian", value: "Syrian" },
    { label: "Emirati", value: "Emirati" },
    { label: "American", value: "American" },
    { label: "British", value: "British" },
    { label: "French", value: "French" },
    { label: "German", value: "German" },
    { label: "Italian", value: "Italian" },
    { label: "Spanish", value: "Spanish" },
    { label: "Canadian", value: "Canadian" },
    { label: "Australian", value: "Australian" },
    { label: "Indian", value: "Indian" },
    { label: "Pakistani", value: "Pakistani" },
    { label: "Egyptian", value: "Egyptian" },
    { label: "Lebanese", value: "Lebanese" },
    { label: "Jordanian", value: "Jordanian" },
    { label: "Saudi", value: "Saudi" },
    { label: "Other", value: "Other" },
  ];

  const handleCountryChange = (value) => {
    // Prevent selection of disabled UAE option
    if (value === "UAE (Coming Soon)") {
      return;
    }
    // Update country and reset city in a single state update
    setFormData((prev) => ({
      ...prev,
      country: value,
      city: "", // Reset city when country changes
    }));
    // Clear country error if exists
    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: null }));
    }
  };

  const getAvailableCities = () => {
    if (!formData.country) {
      return [{ label: "Select City", value: "" }];
    }
    return cities[formData.country] || [{ label: "Select City", value: "" }];
  };

  const handleInputChange = (field, value) => {
    if (field === "phoneNumber") {
      setFormData({ ...formData, phoneNumber: value });
    } else {
      setFormData({ ...formData, [field]: value });
    }
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleInfluencerChange = (field, value) => {
    setInfluencerData({ ...influencerData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleBusinessChange = (field, value) => {
    setBusinessData({ ...businessData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const toggleCategory = (category) => {
    const currentCategories = influencerData.categories;
    if (currentCategories.includes(category)) {
      handleInfluencerChange(
        "categories",
        currentCategories.filter((cat) => cat !== category)
      );
    } else if (currentCategories.length < 3) {
      handleInfluencerChange("categories", [...currentCategories, category]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Common validations
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (userType === "influencer") {
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      }
      if (!formData.nationality) {
        newErrors.nationality = "Nationality is required";
      }
      if (!formData.country) {
        newErrors.country = "Country is required";
      }
      if (!formData.city) {
        newErrors.city = "City is required";
      }
      if (!influencerData.dateOfBirth) {
        newErrors.dateOfBirth = "Date of birth is required";
      }
      if (!influencerData.instagram.trim()) {
        newErrors.instagram = "Instagram username or URL is required";
      }
      if (!influencerData.followers) {
        newErrors.followers = "Please select your follower count";
      }
      if (!influencerData.categories.length) {
        newErrors.categories = "Please select at least one category";
      } else if (influencerData.categories.length > 3) {
        newErrors.categories = "You can select maximum 3 categories";
      }
    } else {
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
      }
      if (!businessData.companyName.trim()) {
        newErrors.companyName = "Company name is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;

      let payload = {};
      let endpoint = "";

      if (userType === "influencer") {
        endpoint = "/admin/users/influencer";
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          nationality: formData.nationality,
          country: formData.country,
          city: formData.city,
          dateOfBirth: influencerData.dateOfBirth,
          instagram: influencerData.instagram,
          facebook: influencerData.facebook || null,
          followers: influencerData.followers,
          categories: influencerData.categories,
        };
      } else {
        endpoint = "/admin/users/business";
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber,
          companyName: businessData.companyName,
        };
      }

      const response = await Api.post(endpoint, payload);

      if (response.data.success) {
        toast.success(
          `${
            userType === "influencer" ? "Influencer" : "Business"
          } user created successfully`
        );
        handleClose();
        if (onUserCreated) {
          onUserCreated();
        }
      } else {
        toast.error(response.data.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Create user error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.email?.[0] ||
        "Failed to create user";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      phoneNumber: "",
      country: "",
      city: "",
      nationality: "",
    });
    setInfluencerData({
      dateOfBirth: "",
      instagram: "",
      facebook: "",
      followers: "",
      categories: [],
      profileImage: null,
    });
    setBusinessData({
      companyName: "",
    });
    setErrors({});
    toggle();
  };

  return (
    <Modal isOpen={modal} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Create New User</ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Form>
          {/* User Type Selection */}
          <FormGroup>
            <Label>User Type *</Label>
            <div className="d-flex gap-3">
              <Button
                color={userType === "influencer" ? "primary" : "secondary"}
                onClick={() => setUserType("influencer")}
                style={{ flex: 1 }}
              >
                Influencer
              </Button>
              <Button
                color={userType === "business" ? "primary" : "secondary"}
                onClick={() => setUserType("business")}
                style={{ flex: 1 }}
              >
                Business
              </Button>
            </div>
          </FormGroup>

          {/* Common Fields */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  invalid={!!errors.firstName}
                />
                {errors.firstName && (
                  <div className="invalid-feedback d-block">
                    {errors.firstName}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  invalid={!!errors.lastName}
                />
                {errors.lastName && (
                  <div className="invalid-feedback d-block">
                    {errors.lastName}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              invalid={!!errors.email}
            />
            {errors.email && (
              <div className="invalid-feedback d-block">{errors.email}</div>
            )}
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  invalid={!!errors.password}
                />
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
                <small className="text-muted">
                  Must be at least 8 characters
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  invalid={!!errors.confirmPassword}
                />
                {errors.confirmPassword && (
                  <div className="invalid-feedback d-block">
                    {errors.confirmPassword}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Influencer Specific Fields */}
          {userType === "influencer" && (
            <>
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  invalid={!!errors.phone}
                />
                {errors.phone && (
                  <div className="invalid-feedback d-block">{errors.phone}</div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Nationality *</Label>
                <Input
                  type="select"
                  value={formData.nationality}
                  onChange={(e) =>
                    handleInputChange("nationality", e.target.value)
                  }
                  invalid={!!errors.nationality}
                >
                  {nationalities.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {errors.nationality && (
                  <div className="invalid-feedback d-block">
                    {errors.nationality}
                  </div>
                )}
              </FormGroup>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Country *</Label>
                    <Input
                      type="select"
                      value={formData.country}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      invalid={!!errors.country}
                    >
                      {countries.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled || false}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    {errors.country && (
                      <div className="invalid-feedback d-block">
                        {errors.country}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>City *</Label>
                    <Input
                      type="select"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      disabled={!formData.country}
                      invalid={!!errors.city}
                    >
                      {getAvailableCities().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    {errors.city && (
                      <div className="invalid-feedback d-block">
                        {errors.city}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={influencerData.dateOfBirth}
                  onChange={(e) =>
                    handleInfluencerChange("dateOfBirth", e.target.value)
                  }
                  max={new Date().toISOString().split("T")[0]}
                  invalid={!!errors.dateOfBirth}
                />
                {errors.dateOfBirth && (
                  <div className="invalid-feedback d-block">
                    {errors.dateOfBirth}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Instagram Username or URL *</Label>
                <Input
                  type="text"
                  value={influencerData.instagram}
                  onChange={(e) =>
                    handleInfluencerChange("instagram", e.target.value)
                  }
                  placeholder="username or url"
                  invalid={!!errors.instagram}
                />
                {errors.instagram && (
                  <div className="invalid-feedback d-block">
                    {errors.instagram}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Facebook Username or URL (Optional)</Label>
                <Input
                  type="text"
                  value={influencerData.facebook}
                  onChange={(e) =>
                    handleInfluencerChange("facebook", e.target.value)
                  }
                  placeholder="username or url"
                />
              </FormGroup>

              <FormGroup>
                <Label>Followers Count *</Label>
                <Input
                  type="select"
                  value={influencerData.followers}
                  onChange={(e) =>
                    handleInfluencerChange("followers", e.target.value)
                  }
                  invalid={!!errors.followers}
                >
                  {followersOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {errors.followers && (
                  <div className="invalid-feedback d-block">
                    {errors.followers}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Categories * (Select up to 3)</Label>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {categories.map((category) => {
                    const isSelected =
                      influencerData.categories.includes(category);
                    const isDisabled =
                      influencerData.categories.length >= 3 && !isSelected;
                    return (
                      <Button
                        key={category}
                        size="sm"
                        color={isSelected ? "primary" : "outline-secondary"}
                        onClick={() => toggleCategory(category)}
                        disabled={isDisabled}
                        style={{
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                        }}
                      >
                        {category}
                      </Button>
                    );
                  })}
                </div>
                {errors.categories && (
                  <div className="invalid-feedback d-block mt-2">
                    {errors.categories}
                  </div>
                )}
                <small className="text-muted">
                  Selected: {influencerData.categories.length}/3
                </small>
              </FormGroup>
            </>
          )}

          {/* Business Specific Fields */}
          {userType === "business" && (
            <>
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="text"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  invalid={!!errors.phoneNumber}
                />
                {errors.phoneNumber && (
                  <div className="invalid-feedback d-block">
                    {errors.phoneNumber}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Company Name *</Label>
                <Input
                  type="text"
                  value={businessData.companyName}
                  onChange={(e) =>
                    handleBusinessChange("companyName", e.target.value)
                  }
                  placeholder="Company Name"
                  invalid={!!errors.companyName}
                />
                {errors.companyName && (
                  <div className="invalid-feedback d-block">
                    {errors.companyName}
                  </div>
                )}
              </FormGroup>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Creating..." : "Create User"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateUserModal;
